import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Code, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Mailbox, useMailboxSignatureMutation, useMailboxesQuery } from '@/client/api/inbox/inbox';
import { useUserStore } from '@/client/stores/user';
import { SIGNATURE_DESIGNS, SignatureFields, defaultFields, readFields, renderSignature } from './signatureDesigns';

/** The signed-in admin's name, which {{name}} becomes when they send. */
const useSenderName = () => {
  const user = useUserStore((s) => s.user);
  return `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim();
};

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * The signature exactly as it is sent: raw HTML in a sandboxed frame (no
 * scripts), sized to its content. The rich-text editor would drop the tables
 * and image sizes a signature is built from.
 */
export function SignaturePreview({ html, name }: { html: string; name: string }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(80);
  const doc = `<!doctype html><html><body style="margin:12px;background:#fff;">${html
    .split('{{name}}')
    .join(escapeHtml(name || 'Your name'))}</body></html>`;
  const fit = () => {
    const body = frame.current?.contentDocument?.body;
    if (body) setHeight(body.scrollHeight + 24);
  };
  return (
    <iframe
      ref={frame}
      title="Signature preview"
      sandbox="allow-same-origin"
      srcDoc={doc}
      onLoad={() => {
        fit();
        // Logos load after the frame does.
        frame.current?.contentDocument?.querySelectorAll('img').forEach((img) => img.addEventListener('load', fit));
      }}
      style={{ height }}
      className="w-full rounded-md border border-gray-200 bg-white"
    />
  );
}

/**
 * "Add signature" switch with a read-only preview of the mailbox's signature as
 * this admin will send it. The API adds the signature; this only shows it.
 */
export function SignatureToggle({
  mailbox,
  on,
  onChange,
  preview = true,
}: {
  mailbox?: Mailbox;
  on: boolean;
  onChange: (on: boolean) => void;
  preview?: boolean;
}) {
  const name = useSenderName();
  const html = mailbox?.signature_html?.trim() ? mailbox.signature_html : '';
  if (!mailbox) return null;
  if (!html) {
    return <p className="text-xs text-gray-500">{mailbox.email} has no signature yet. Set one under Email &gt; Signatures.</p>;
  }
  return (
    <div className="space-y-1">
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} className="rounded border-gray-300" />
        Add the {mailbox.email} signature
      </label>
      {on && preview && <SignaturePreview html={html} name={name} />}
    </div>
  );
}

const FIELDS: { key: keyof SignatureFields; label: string; placeholder?: string }[] = [
  { key: 'team', label: 'Team or role', placeholder: 'FeelyFeely Sales' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone', placeholder: '+233 ...' },
  { key: 'whatsapp', label: 'WhatsApp', placeholder: '+233 ...' },
  { key: 'website', label: 'Website', placeholder: 'feelyfeely.com' },
  { key: 'location', label: 'Location', placeholder: 'Accra, Ghana' },
  { key: 'tagline', label: 'Tagline', placeholder: 'Ghana’s trust-first intelligent marketplace' },
  { key: 'handle', label: 'Social handle', placeholder: '@feelyfeelygh' },
  { key: 'instagram', label: 'Instagram link' },
  { key: 'facebook', label: 'Facebook link' },
  { key: 'x', label: 'X link' },
  { key: 'linkedin', label: 'LinkedIn link' },
  { key: 'youtube', label: 'YouTube link' },
  { key: 'tiktok', label: 'TikTok link' },
];

/**
 * One signature per mailbox; {{name}} becomes whoever sends. Pick a design and
 * fill the fields, or edit the HTML directly (which leaves the designs).
 */
export function SignatureSettings() {
  const queryClient = useQueryClient();
  const mailboxes = useMailboxesQuery().data || [];
  const save = useMailboxSignatureMutation();
  const senderName = useSenderName();
  const [email, setEmail] = useState('');
  const current = mailboxes.find((m) => m.email === email) ?? mailboxes[0];
  const saved = current?.signature_html ?? '';
  // null = hand-written HTML, edited in the source box.
  const [fields, setFields] = useState<SignatureFields | null>(null);
  const [draft, setDraft] = useState('');
  const [showHtml, setShowHtml] = useState(false);

  useEffect(() => {
    if (!current) return;
    const f = saved.trim() ? readFields(saved) : defaultFields(current);
    setFields(f);
    setDraft(f ? renderSignature(f) : saved);
    setShowHtml(!f);
  }, [current?.email, saved]);

  if (!current) return <p className="text-sm text-gray-500">No mailboxes are set up.</p>;

  const update = (patch: Partial<SignatureFields>) => {
    const next = { ...(fields ?? defaultFields(current)), ...patch };
    setFields(next);
    setDraft(renderSignature(next));
  };

  const onSave = () =>
    save.mutate(
      { email: current.email, signature_html: draft },
      {
        onSuccess: () => {
          toast.success(`Signature saved for ${current.email}`, { richColors: true });
          queryClient.invalidateQueries({ queryKey: ['inbox', 'mailboxes'] });
        },
        onError: (error) => toast.error(error instanceof Error ? error.message : 'The signature was not saved', { richColors: true }),
      },
    );

  const field = 'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="space-y-5 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="max-w-sm">
        <label htmlFor="sig-mailbox" className="mb-1 block text-xs text-gray-500">Mailbox</label>
        <select id="sig-mailbox" value={current.email} onChange={(e) => setEmail(e.target.value)} className={field}>
          {mailboxes.map((m) => (
            <option key={m.email} value={m.email}>
              {m.name} &lt;{m.email}&gt;
            </option>
          ))}
        </select>
      </div>

      <div>
        <p className="mb-2 text-xs text-gray-500">Design</p>
        <div className="flex flex-wrap gap-2">
          {SIGNATURE_DESIGNS.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => update({ design: d.id })}
              aria-pressed={fields?.design === d.id}
              className={`rounded-md border px-3 py-1.5 text-sm ${
                fields?.design === d.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {d.label}
            </button>
          ))}
          {!fields && <span className="self-center text-xs text-amber-700">Custom HTML. Picking a design replaces it.</span>}
        </div>
      </div>

      {fields && (
        <div className="grid gap-3 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label htmlFor={`sig-${f.key}`} className="mb-1 block text-xs text-gray-500">{f.label}</label>
              <input
                id={`sig-${f.key}`}
                type="text"
                value={fields[f.key] ?? ''}
                placeholder={f.placeholder}
                onChange={(e) => update({ [f.key]: e.target.value })}
                className={field}
              />
            </div>
          ))}
        </div>
      )}

      <div>
        <p className="mb-1 text-xs text-gray-500">Preview, as {senderName || 'you'} will send it</p>
        <SignaturePreview html={draft} name={senderName} />
        <p className="mt-1 text-xs text-gray-500">
          <code className="rounded bg-gray-100 px-1">{'{{name}}'}</code> becomes the sender's name. It is added below every email sent from {current.email}.
        </p>
      </div>

      <div>
        <button type="button" onClick={() => setShowHtml((v) => !v)} className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
          <Code className="h-3.5 w-3.5" />
          {showHtml ? 'Hide HTML' : 'Edit HTML'}
        </button>
        {showHtml && (
          <textarea
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setFields(null);
            }}
            rows={10}
            spellCheck={false}
            aria-label="Signature HTML"
            className={`${field} mt-2 font-mono text-xs`}
          />
        )}
      </div>

      <div className="flex justify-end">
        <Button type="button" onClick={onSave} disabled={save.isPending || draft === saved} className="gap-2">
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save signature
        </Button>
      </div>
    </div>
  );
}
